// Podcast Ad's cast stage (live's `cast-stage` and `podcast-still-card`; not captured, since live's
// stage needs a paid Approve script, so built from live's code): "Cast & set", the five stills,
// each's ↻ and the master's Fix label, and who the director cast.
import { Button, Group, Stack, Text } from "@mantine/core";
import { useState } from "react";

import type { DraftStill, ShotSpec, StillName } from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { STILL_ORDER, stillWaiting } from "./podcast-draft";
import { StillCard } from "./StillCard";
import classes from "./podcast.module.css";

/** Live's labels (`STILL_LABELS`) and words (`library-view/constants.ts`, `still-card`). */
export const STILL_LABELS: Readonly<Record<StillName, string>> = {
  master: "Master",
  host_close: "Host close-up",
  guest_close: "Guest close-up",
  guest_hold: "Guest holds it",
  end_card: "End card",
};

const PENDING: Omit<DraftStill, "name"> = { status: "pending", url: null, problem: null };

/** A made still in the shared lightbox (issue 828, bug 25), named by live's label. */
function stillItem(still: DraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface CastStageProps {
  stills: readonly DraftStill[];
  spec: ShotSpec | null;
  /** The shot director casts the set: every still waits on it (live's `isShotDirecting`). */
  planning: boolean;
  /** Something works or an ask is on its way: ↻ and Fix label wait (live's `isBusy`). */
  busy: boolean;
  /** The draft's start over is on its way. */
  resetting: boolean;
  onRegenerate: (name: StillName) => void;
  onFixLabel: () => void;
  onStartOver: () => void;
}

/**
 * Live's cast stage: "Cast & set", the five stills in live's order (each a 9:16 card with its
 * label under it), then the host and the guest as the director cast them. A done still opens
 * in the shared lightbox (issue 828, bug 25), stepping through the made ones in live's order.
 * Ours also offers "Start over" (the draft outlives the panel, where live's closing the panel was
 * its only way back).
 */
export function CastStage(props: CastStageProps) {
  const { stills, spec, planning, busy, resetting, onRegenerate, onFixLabel, onStartOver } = props;
  const [preview, setPreview] = useState<StillName | null>(null);
  const [confirming, setConfirming] = useState(false);
  // The made stills in live's order: what the lightbox steps through.
  const shown = STILL_ORDER.flatMap((name) => {
    const still = stills.find((each) => each.name === name);
    return still?.url ? [{ ...still, url: still.url }] : [];
  });
  return (
    <Stack gap={12} aria-label="Cast & set" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>Cast &amp; set</Text>
        {/* Held while anything is being made: the server refuses a start over then, as live's form
            is locked, so an image in flight is never charged for a draft that is gone. */}
        <Button
          variant="subtle"
          size="compact-sm"
          disabled={resetting || busy || planning}
          onClick={() => setConfirming(true)}
        >
          Start over
        </Button>
      </Group>
      <div className={classes.stills}>
        {STILL_ORDER.map((name) => {
          const still = stills.find((each) => each.name === name) ?? { name, ...PENDING };
          return (
            <StillCard
              key={name}
              label={STILL_LABELS[name]}
              still={still}
              waiting={planning || stillWaiting(name, stills)}
              busy={busy}
              onRegenerate={() => onRegenerate(name)}
              onFixLabel={name === "master" ? onFixLabel : null}
              onPreview={() => setPreview(name)}
            />
          );
        })}
      </div>
      {spec === null ? null : (
        <Stack gap={2}>
          <Text className={classes.castLine}>
            Host: {spec.cast.host.identity} · wearing {spec.cast.host.outfit}
          </Text>
          <Text className={classes.castLine}>
            Guest: {spec.cast.guest.identity} · wearing {spec.cast.guest.outfit}
          </Text>
        </Stack>
      )}
      <MediaPreview items={shown.map(stillItem)} current={preview} onShow={(name) => setPreview(name as StillName | null)} />
      <ConfirmModal
        opened={confirming}
        title="Start over"
        confirmLabel="Start over"
        pending={resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onStartOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">The script, the cast and the five images are cleared, and the form starts empty.</Text>
      </ConfirmModal>
    </Stack>
  );
}
